import Swiper from 'swiper';
import {Navigation} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

// Modal with Swiper
async function openGalleryModal(images, startIndex = 0, spinner = null) {
    // Send images to server to render modal via Twig
    let modalElement = null;
    let modalSwiper = null;

    try {
        const body = `action=render_gallery_modal&images=${encodeURIComponent(JSON.stringify(images))}&startIndex=${encodeURIComponent(String(startIndex))}`;
        const response = await fetch(themeVars.ajax_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body,
            credentials: 'same-origin',
        });

        const contentType = response.headers.get('content-type') || '';

        if (!response.ok) {
            const text = await response.text();
            console.error('AJAX request failed:', response.status, text.slice(0, 200));
            throw new Error('AJAX request failed');
        }

        let json = null;
        if (contentType.includes('application/json') || contentType.includes('+json')) {
            json = await response.json();
        } else {
            const text = await response.text();
            try {
                json = JSON.parse(text);
            } catch (e) {
                console.error('Failed to parse JSON response:', e);
                console.error('Expected JSON but got non-JSON response (first 200 chars):', text.slice(0, 200));
                throw new Error('Server returned non-JSON response');
            }
        }

        if (!json || !json.success) {
            throw new Error('Invalid response from server');
        }

        const modalHTML = json.data;
        const wrapper = document.createElement('div');
        wrapper.innerHTML = modalHTML.trim();
        modalElement = wrapper.firstElementChild;
        if (!modalElement) throw new Error('No modal element returned');

        document.body.appendChild(modalElement);

        // find swiper container and initialize
        const modalSwiperContainer = modalElement.querySelector('.gallery-modal-swiper');
        const downloadLink = modalElement.querySelector('.gallery-modal__download');

        modalSwiper = new Swiper(modalSwiperContainer, {
            modules: [Navigation],
            slidesPerView: 1,
            spaceBetween: 2,
            navigation: {
                nextEl: modalElement.querySelector('.swiper-button-next'),
                prevEl: modalElement.querySelector('.swiper-button-prev'),
            },
            on: {
                slideChange: (swiper) => {
                    if (!swiper || !swiper.slides || typeof swiper.activeIndex !== 'number') return;
                    const currentSlide = swiper.slides[swiper.activeIndex];
                    if (!currentSlide) return;
                    const img = currentSlide.querySelector ? currentSlide.querySelector('img') : null;
                    const video = currentSlide.querySelector ? currentSlide.querySelector('video') : null;
                    if (!downloadLink) return;
                    if (img) {
                        downloadLink.href = img.src || '';
                        downloadLink.download = img.alt || 'image';
                    } else if (video) {
                        const source = video.querySelector ? video.querySelector('source') : null;
                        const src = source ? source.getAttribute('src') : (video.getAttribute('src') || '');
                        downloadLink.href = src || '';
                        downloadLink.download = video.getAttribute('aria-label') || video.getAttribute('title') || 'video';
                    }
                },
            },
            initialSlide: parseInt(modalElement.getAttribute('data-start-index') || startIndex, 10),
        });

        modalElement.classList.add('gallery-modal--open');
        if (spinner) spinner.classList.remove('active');

        const btnNext = modalElement.querySelector('.swiper-button-next');
        if (btnNext && typeof btnNext.focus === 'function') btnNext.focus();

        function close() {
            try {
                modalSwiper.destroy(true, true);
            } catch (e) {
                console.error('Error destroying Swiper instance:', e);
                // ignore
            }
            modalElement.classList.remove('gallery-modal--open');
            modalElement.remove();
            document.removeEventListener('keydown', onKey);
            document.body.classList.remove('no-scroll');
        }

        function onKey(e) {
            if (e.key === 'Escape') close();
            if (e.key === 'ArrowRight') modalSwiper.slideNext();
            if (e.key === 'ArrowLeft') modalSwiper.slidePrev();
        }

        const closeBtn = modalElement.querySelector('.gallery-modal__close');
        const backdrop = modalElement.querySelector('.gallery-modal__backdrop');
        const inner = modalElement.querySelector('.gallery-modal__inner');
        if (closeBtn) closeBtn.addEventListener('click', close);
        if (backdrop) backdrop.addEventListener('click', close);
        if (inner) inner.addEventListener('click', (e) => {
            e.stopPropagation();
            if (e.target !== inner) return; // allow close button to work
            close();
        });
        document.addEventListener('keydown', onKey);
    } catch (err) {
        // fallback: log error
        console.error(err);
    }
}

// attach click handlers to gallery thumbnails to open modal
function attachGalleryModalHandlers() {
    // only attach modal handlers for gallery blocks that opt-in via `data-modal` attribute
    const modalBlocks = document.querySelectorAll('[data-modal]');
    const spinner = document.querySelector('[data-spinner-js]') ?? null;

    modalBlocks.forEach(block => {
        const slides = Array.from(block.querySelectorAll('[data-gallery-item]'));
        const images = slides.map(slide => {
            // support images and videos inside slide
            const img = slide.querySelector('img');
            const video = slide.querySelector('video');
            if (video) {
                // try to get source URL from <source> or video.src
                const source = video.querySelector('source');
                const src = source ? source.getAttribute('src') : (video.getAttribute('src') || '');
                const poster = video.getAttribute('poster') || '';
                return { src: src, alt: video.getAttribute('aria-label') || video.getAttribute('title') || '', description: slide.dataset.description || '', type: 'video', poster };
            }

            return { src: img ? img.getAttribute('src') : '', alt: img ? img.getAttribute('alt') : img ? img.getAttribute('title') : '', description: slide.dataset.description || '', type: 'image' };
        });

        slides.forEach((slide, idx) => {
            slide.addEventListener('click', (e) => {
                e.preventDefault();
                document.body.classList.add('no-scroll');
                if (spinner) spinner.classList.add('active');
                openGalleryModal(images, idx, spinner);
            });
            slide.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    document.body.classList.add('no-scroll');
                    if (spinner) spinner.classList.add('active');
                    openGalleryModal(images, idx, spinner);
                }
            });
        });
    });
}

export default attachGalleryModalHandlers;
